import {
  LanguageItem,
  LanguageProficiency,
} from "../../models/languageSkill.model";
import { BackendHttpService } from "../backendHttpService";

/**
 * Language Dictionary Service
 *
 * Pure read-only service for accessing language dictionary data.
 * This service handles both languages and proficiency levels.
 * Designed for React Query integration and composition patterns.
 */

// Helper service for languages
class LanguageService extends BackendHttpService {
  constructor() {
    super("Language");
  }

  async getLanguages(): Promise<LanguageItem[]> {
    return await this.get<LanguageItem[]>("");
  }
}

// Helper service for proficiencies
class ProficiencyService extends BackendHttpService {
  constructor() {
    super("LanguageProficiency");
  }

  async getProficiencies(): Promise<LanguageProficiency[]> {
    return await this.get<LanguageProficiency[]>("");
  }
}

export class LanguageDictionaryService {
  private languageService: LanguageService;
  private proficiencyService: ProficiencyService;

  constructor() {
    this.languageService = new LanguageService();
    this.proficiencyService = new ProficiencyService();
  }

  /**
   * Get all languages from dictionary, sorted alphabetically
   * GET /Language
   */
  async getLanguageDictionary(): Promise<LanguageItem[]> {
    try {
      const items = await this.languageService.getLanguages();

      return items.sort((a, b) => {
        const nameA = a.englishName.toUpperCase();
        const nameB = b.englishName.toUpperCase();
        if (nameA < nameB) return -1;
        if (nameA > nameB) return 1;
        return 0;
      });
    } catch (error) {
      console.error("Error getting language dictionary:", error);
      throw error;
    }
  }

  /**
   * Get all language proficiencies, sorted by level (A1, A2, B1, B2, C1, C2)
   * GET /LanguageProficiency
   */
  async getLanguageProficiencies(): Promise<LanguageProficiency[]> {
    try {
      const proficiencies = await this.proficiencyService.getProficiencies();

      return proficiencies.sort((a, b) => {
        // Sort by proficiency level (A1, A2, B1, B2, C1, C2)
        const levelA = a.level.charAt(0); // Get first character
        const levelB = b.level.charAt(0);

        if (levelA !== levelB) {
          return levelA.localeCompare(levelB);
        }

        // If same level letter, sort by number
        const numA = parseInt(a.level.charAt(1)) || 0;
        const numB = parseInt(b.level.charAt(1)) || 0;

        return numA - numB;
      });
    } catch (error) {
      console.error("Error getting language proficiencies:", error);
      throw error;
    }
  }

  /**
   * Clear all cached dictionary data
   * Used when dictionaries are modified through admin operations
   * Note: With React Query, use cache invalidation hooks instead
   */
  clearCache(): void {
    // React Query handles caching - this method is kept for backward compatibility
    // Use useCacheInvalidation hook instead: invalidateLanguages() or invalidateLanguageProficiencies()
  }
}

// Create and export a singleton instance for consistency
export const languageDictionaryService = new LanguageDictionaryService();
